import { useParams, Navigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { FinalCTA } from "@/components/site/CTAs";
import { INDUSTRIES } from "@/data/industries";

const TCard = ({ dot, cat, title, body, stake }) => (
  <div className="card">
    <div className="cat">
      <span className="d" style={{ background: dot }} />
      {cat}
    </div>
    <h3>{title}</h3>
    <p>{body}</p>
    <div className="stake">{stake}</div>
  </div>
);

const IndustryPage = () => {
  const { slug } = useParams();
  const data = INDUSTRIES[slug];

  if (!data) return <Navigate to="/" replace />;

  const testIdPrefix = `industry-${slug}`;

  return (
    <>
      <Seo title={data.seoTitle} description={data.seoDescription} path={`/for/${data.slug}`} />
      <Header />

      <section className="hero">
        <div className="glow" />
        <div className="container">
          <Reveal>
            <div className="eyebrow eyebrow-light">{data.eyebrow}</div>
            <h1 style={{ marginTop: 16 }}>
              {data.h1Line1}<br />{data.h1Line2}<br />
              <span className="gtext">Ask before you commit.</span>
            </h1>
            <p className="sub">{data.sub}</p>
            <div className="cta">
              <Link className="pill" to={`/apply?src=${data.slug}`} data-testid={`${testIdPrefix}-hero-cta`}>Book a free demo</Link>
              <a className="ghost" href="#decisions" style={{ color: "#fff" }}>Or see what it answers <span className="arr">↓</span></a>
            </div>
            <div className="chips">
              <span>Reads Xero / QB / WorkflowMax</span><span>Read-only</span><span>No lock-in</span><span>Aussie-built</span>
            </div>
          </Reveal>

          <Reveal>
            <div className="pcard">
              <div className="top">
                <span><span className="dot" />Ask Clearview</span>
                <span className="refused">Refused 35 in 24h</span>
              </div>
              <div className="body">
                <div className="ask"><span>{data.ask.question}</span></div>
                <div className="lab">Answer</div>
                <div className="ans">
                  <span className="no">{data.ask.verdictShort}</span> {data.ask.verdictRest}
                </div>
                <div className="best">
                  <div className="l">⚡ Best move now</div>
                  <p>{data.ask.bestBody}</p>
                </div>
                <div className="opts">
                  <div className="opt"><div className="tt">Safest</div><div className="h">{data.ask.options.safest}</div></div>
                  <div className="opt"><div className="tt">Growth</div><div className="h">{data.ask.options.growth}</div></div>
                  <div className="opt"><div className="tt">Bold</div><div className="h">{data.ask.options.bold}</div></div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="band-grey pad" id="decisions">
        <div className="container">
          <Reveal className="center">
            <div className="eyebrow">The calls that actually cost you</div>
            <h2 style={{ fontSize: "clamp(28px,4.4vw,44px)", color: "var(--navy)", marginTop: 14 }}>
              {data.decisionsHeading}
            </h2>
            <p className="sub-h">{data.decisionsSub}</p>
          </Reveal>
          <Reveal className="grid g3" style={{ marginTop: 44 }}>
            {data.cards.map((c, i) => (
              <TCard key={i} dot={c.dot} cat={c.cat} title={c.title} body={c.body} stake={c.stake} />
            ))}
          </Reveal>
        </div>
      </section>

      <section className="pad center">
        <div className="container" style={{ maxWidth: 760 }}>
          <Reveal>
            <div className="eyebrow">{data.whyEyebrow}</div>
            <h2 style={{ fontSize: "clamp(26px,4vw,38px)", color: "var(--navy)", marginTop: 14 }}>
              It talks like your best <span className="gtext">{data.whyRole}</span>, not your accountant.
            </h2>
            <p className="sub-h">
              No jargon, no "consult a professional," no dashboard to learn. You ask in plain English, it gives you a straight call and the next move — read-only, so it can't touch your money.
            </p>
          </Reveal>
        </div>
      </section>

      <FinalCTA
        title="Can you afford the next move?"
        highlight="Ask it."
        body="Connect read-only and get a straight answer on your real numbers — in the next few minutes."
        testIdPrefix={`${testIdPrefix}-final`}
        to={`/apply?src=${data.slug}`}
      />

      <Footer />
    </>
  );
};

export default IndustryPage;
